import { Typography } from '@mui/joy';
import { ColDef, FilterModel } from 'ag-grid-community';
import { uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AttributeType } from 'companySettings/enums';
import { AgGridTable, Button, Error, PageTitle } from 'core/components';
import Breadcrumbs from 'core/components/Breadcrumbs';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import {
  useGetPartFinderCatalogFacets,
  useGetPartFinderFamilyDefinition,
  useGetPartFinderReels,
} from 'partFinder/hooks';
import { partFinderCatalogPath, partFinderFamilyPath } from 'partFinder/routes/routePaths';
import {
  IPartFinderFamilyDefinition,
  IPartFinderReel,
  IPartFinderReelsPage,
} from 'partFinder/types';
import {
  getErrorStatusCode,
  selectionFromSearch,
  selectionToSearch,
  usePartFinderReturn,
} from 'partFinder/utils';
import { Level, Size } from 'theme/enums';

import { AddToQuoteOrderButton } from './AddToQuoteOrder';
import { formatAttributeValues } from './attributeValues';
import { PreAppliedFilters } from './PreAppliedFilters';
import { getReelsLoadState } from './reelsLoadState';
import { CopyValueCell, isButtonMouseEvent } from '../CopyValueCell';
import { PartNumberCell } from '../PartNumberCell';

// The choices a checkbox-list column's filter offers: the ones the catalog or the family has, then
// any other a row has, so every row can be picked. With them, ticking only what the catalog and the
// filter page picked still leaves choices unticked, and AG Grid shows it as a filter even when
// every row matches - a list with every choice ticked reads as no filter at all.
const toFilterChoices = (
  known: (string | null)[],
  rowValues: (string | null)[],
  withBlank = false
) => [
  ...uniq([...known, ...rowValues].filter((value): value is string => Boolean(value))),
  ...(withBlank ? [null] : []),
];

// While the family is still loading, the filter choices take no values from the rows (see
// choiceRecords).
const NO_RECORDS: IPartFinderReel[] = [];

interface IProps {
  familyId: number;
}

export const PartFinderReelResults = ({ familyId }: IProps) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { returnTo, navigateWithReturn, cancel } = usePartFinderReturn();

  const selection = useMemo(() => selectionFromSearch(search), [search]);

  const {
    data: loadedDefinition,
    isError: isDefinitionError,
    error: definitionError,
  } = useGetPartFinderFamilyDefinition(familyId, selection);
  const {
    records,
    extraData,
    isLoading,
    isError: isReelsError,
    statusCode: reelsStatusCode,
    hasNextPage,
    currentPage,
  } = useGetPartFinderReels(familyId, selection);

  // The last definition that loaded, kept across a failed request (react-query drops the previous
  // data once the new request fails), so a failed refresh leaves the grid in place. The reels keep
  // the pages they loaded when a later one fails.
  const [definition, setDefinition] = useState<IPartFinderFamilyDefinition>();

  useEffect(() => {
    if (loadedDefinition) {
      setDefinition(loadedDefinition);
    }
  }, [loadedDefinition]);

  // The error replaces the page only when the failed request has nothing loaded to show for it;
  // a refresh, or a later page, that fails after the first load leaves the grid in place.
  const loadError =
    (isDefinitionError && !definition && definitionError) ||
    (isReelsError && !currentPage && { response: { status: reelsStatusCode } }) ||
    null;
  const errorStatusCode = loadError ? getErrorStatusCode(loadError) : null;
  const { data: facets } = useGetPartFinderCatalogFacets();

  // What the catalog and the filter page narrowed to, as the grid's column filters: the locations
  // and manufacturers by name, each attribute's picked values, and the number picked for a
  // Number & Range one.
  const preAppliedFilterModel = useMemo(() => {
    const filterModel: FilterModel = {};
    const locationNames = (facets?.locations ?? [])
      .filter(({ id }) => selection.locationIds.includes(id))
      .map(({ name }) => name);

    if (locationNames.length) {
      filterModel.locationName = { filterType: 'set', values: locationNames };
    }

    const manufacturerNames = (facets?.manufacturers ?? [])
      .filter(({ id }) => selection.manufacturerIds.includes(id))
      .map(({ name }) => name);

    if (manufacturerNames.length) {
      filterModel.manufacturerName = { filterType: 'set', values: manufacturerNames };
    }

    (definition?.attributes ?? []).forEach((attribute) => {
      const colId = `attribute${attribute.id}`;

      if (AttributeType.NUMBER_RANGE === attribute.type) {
        const number = selection.numbers?.[attribute.id];

        if (number !== undefined && number !== '' && !Number.isNaN(Number(number))) {
          filterModel[colId] = { filterType: 'number', type: 'equals', filter: Number(number) };
        }

        return;
      }

      const values = selection.values?.[attribute.id] ?? [];

      if (values.length) {
        filterModel[colId] = { filterType: 'set', values: values.map(String) };
      }
    });

    return filterModel;
  }, [definition, facets, selection]);

  const loadState = getReelsLoadState(
    (extraData ?? []) as unknown as IPartFinderReelsPage[],
    hasNextPage
  );

  // The rows the filter choices are drawn from: none until the whole family is in, so the columns
  // stay the same from page to page - setting them again puts the columns the user hid or pinned
  // back as the saved layout has them - then every row once the last page is in.
  const choiceRecords = loadState.isComplete ? records : NO_RECORDS;

  // The columns the guided search's choices go on (PreAppliedFilters).
  const searchColumnIds = useMemo(
    () => [
      'locationName',
      'manufacturerName',
      ...(definition?.attributes ?? []).map((attribute) => `attribute${attribute.id}`),
    ],
    [definition]
  );

  const columnDefs = useMemo<ColDef<IPartFinderReel>[]>(
    () => [
      // Pinned on the left: what names the part. Then its attributes, then the reel's own columns.
      {
        colId: 'partNumber',
        headerName: t('headerPartNumber'),
        field: 'partNumber',
        filter: 'agTextColumnFilter',
        // What names a row, or free text: nothing to group by. The grid offers the other columns.
        enableRowGroup: false,
        cellRenderer: PartNumberCell,
        cellRendererParams: { suppressMouseEventHandling: isButtonMouseEvent },
        minWidth: 180,
        pinned: 'left',
      },
      {
        colId: 'sku',
        headerName: t('headerSku'),
        field: 'sku',
        filter: 'agTextColumnFilter',
        enableRowGroup: false,
        cellRenderer: CopyValueCell,
        cellRendererParams: {
          copyLabel: t('tooltipCopySku'),
          suppressMouseEventHandling: isButtonMouseEvent,
        },
        minWidth: 160,
        pinned: 'left',
      },
      {
        colId: 'description',
        headerName: t('headerDescription'),
        field: 'description',
        filter: 'agTextColumnFilter',
        enableRowGroup: false,
        // A fixed width: a pinned column that grows would take the room of the scrolling ones.
        width: 280,
        minWidth: 240,
        pinned: 'left',
      },
      {
        colId: 'manufacturerName',
        headerName: t('headerManufacturer'),
        field: 'manufacturerName',
        // The catalog's manufacturers, so the catalog's choice arrives ticked among them.
        filter: 'agSetColumnFilter',
        filterParams: {
          values: toFilterChoices(
            (facets?.manufacturers ?? []).map(({ name }) => name),
            choiceRecords.map(({ manufacturerName }) => manufacturerName)
          ),
        },
        minWidth: 180,
        pinned: 'left',
      },
      ...(definition?.attributes ?? []).map(
        (attribute): ColDef<IPartFinderReel> => ({
          colId: `attribute${attribute.id}`,
          headerName: attribute.name,
          minWidth: 150,
          ...(AttributeType.NUMBER_RANGE === attribute.type
            ? {
                valueGetter: ({ data }) => {
                  const numberValue = data?.values?.find(
                    ({ attributeId }) => attributeId === attribute.id
                  )?.numberValue;

                  return numberValue === null || numberValue === undefined
                    ? null
                    : Number(numberValue);
                },
                filter: 'agNumberColumnFilter',
                filterParams: { defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL },
              }
            : {
                // The part's values for the attribute, each one a choice of the column's filter,
                // as the filter page offers them; a part without one reads as blank.
                valueGetter: ({ data }) => {
                  const values = (data?.values ?? [])
                    .filter(({ attributeId }) => attributeId === attribute.id)
                    .map(({ value, numberValue }) => String(value ?? numberValue));

                  return values.length ? values : null;
                },
                valueFormatter: formatAttributeValues,
                filter: 'agSetColumnFilter',
                // The family's values for the attribute, as the filter page lists them.
                filterParams: {
                  values: toFilterChoices(
                    attribute.values.map(({ value }) => value),
                    choiceRecords.flatMap((record) =>
                      (record.values ?? [])
                        .filter(({ attributeId }) => attributeId === attribute.id)
                        .map(({ value, numberValue }) => String(value ?? numberValue))
                    ),
                    true
                  ),
                },
              }),
        })
      ),
      {
        colId: 'locationName',
        headerName: t('headerLocation'),
        field: 'locationName',
        // The company's stock locations, as the catalog lists them.
        filter: 'agSetColumnFilter',
        filterParams: {
          values: toFilterChoices(
            (facets?.locations ?? []).map(({ name }) => name),
            choiceRecords.map(({ locationName }) => locationName),
            true
          ),
        },
        minWidth: 180,
      },
      {
        colId: 'reelLength',
        headerName: t('headerLength'),
        field: 'reelLength',
        filter: 'agNumberColumnFilter',
        filterParams: { defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL },
        minWidth: 110,
      },
      {
        colId: 'reelId',
        headerName: t('headerReelId'),
        field: 'reelId',
        filter: 'agTextColumnFilter',
        enableRowGroup: false,
        minWidth: 120,
      },
      {
        colId: 'reelType',
        headerName: t('headerReelType'),
        field: 'reelType',
        filter: 'agTextColumnFilter',
        minWidth: 160,
      },
    ],
    [choiceRecords, definition, facets, t]
  );

  // While the family loads: how far it is, and whether the parts the search picked - they load
  // first - are all in yet.
  const getLoadingMessage = () => {
    const progress = { loaded: loadState.loadedParts, total: loadState.familyParts };

    if (isReelsError) {
      return t('messagePartFinderReelsLoadStopped', progress);
    }

    if (!loadState.isNarrowed) {
      return t('messagePartFinderReelsLoadingFamily', progress);
    }

    if (!loadState.areMatchesLoaded) {
      return t('messagePartFinderReelsLoadingMatches', {
        loaded: loadState.loadedParts,
        total: loadState.matchedCount,
      });
    }

    return t('messagePartFinderReelsMatchesLoaded', { ...progress, count: loadState.matchedCount });
  };

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          {
            text: t('labelPartFinderAssets'),
            onClick: () => navigateWithReturn(partFinderCatalogPath()),
          },
          {
            text: definition?.family.name ?? '',
            onClick: () =>
              navigateWithReturn(
                `${partFinderFamilyPath(familyId)}${selectionToSearch(selection)}`
              ),
          },
          { text: t('labelPartFinderPartsList') },
        ]}
      />
      <PageTitle
        title={t('titlePartFinderCatalog')}
        subtitle={t('subtitlePartFinderCatalog')}
        buttons={
          returnTo
            ? [
                <Button
                  key="cancel"
                  text={t('buttonLabelCancel')}
                  onClick={cancel}
                  size={Size.MD}
                />,
              ]
            : undefined
        }
      />

      <Typography level={Level.H3} sx={{ mb: 1 }}>
        {definition?.family.name}
      </Typography>

      {errorStatusCode !== null && <Error statusCode={errorStatusCode} />}

      {!loadState.reelsAvailable && (
        <Typography level={Level.BODY_SM} sx={{ mb: 1 }}>
          {t('messagePartFinderReelsUnavailable')}
        </Typography>
      )}
      {Boolean(currentPage) && !loadState.isComplete && (
        <Typography level={Level.BODY_SM} sx={{ mb: 1 }}>
          {getLoadingMessage()}
        </Typography>
      )}
      {loadState.isComplete && loadState.isTruncated && (
        <Typography level={Level.BODY_SM} sx={{ mb: 1 }}>
          {loadState.matchedCount > loadState.truncatedAt
            ? t('messagePartFinderReelsTruncated', { count: loadState.truncatedAt })
            : t('messagePartFinderReelsTruncatedMatchesFirst', {
                count: loadState.matchedCount,
                limit: loadState.truncatedAt,
              })}
        </Typography>
      )}

      {errorStatusCode === null && (
        <SelectionCountProvider>
          <AgGridClientTableProvider
            tableId="partFinderReels"
            entityType="Reel"
            columnDefs={columnDefs}
            data={records}
            isDataLoading={isLoading}
            loadingPercentage={loadState.percentage}
            dataCurrentPage={currentPage}
            layoutType={TableLayoutType.PART_FINDER_REEL}
            hasLayoutActions
            headerCheckbox
          >
            <PreAppliedFilters
              filterModel={preAppliedFilterModel}
              searchColumnIds={searchColumnIds}
            />
            <AgGridTable
              selectionActionButtons={({ selectedRows, deselectAllRows }) => [
                <AddToQuoteOrderButton
                  key="addToQuoteOrder"
                  reels={selectedRows as IPartFinderReel[]}
                  onSubmitSuccess={deselectAllRows}
                />,
              ]}
            />
          </AgGridClientTableProvider>
        </SelectionCountProvider>
      )}
    </>
  );
};
